import { date } from 'quasar';
import type { dynamicObj } from 'src/pages/assets';
import { useSettingStore } from 'src/store/main';
import { dateFormates, date_pattern_setting } from 'src/constants';
import { dateInput } from 'src/formBuilder/form_elements/formatter/TextInput/index';

const TOKENS = {
  '#': { pattern: '[\\d]' },
} as dynamicObj;

const KEYS = Object.keys(TOKENS);
KEYS.forEach((key) => {
  TOKENS[key].regex = new RegExp(TOKENS[key].pattern);
});

export default {
  methods: {
    isDateMatchMask(date: string, mask: string) {
      if (date === void 0 || date === null || date === '') {
        return false;
      }

      let is_valid = true;
      let valIndex = 0;

      for (let maskIndex = 0; maskIndex < mask.length; maskIndex++) {
        const maskDef = mask[maskIndex];
        const valChar = date[valIndex];

        if (typeof maskDef === 'string' && valChar === maskDef) {
          valIndex++;
          continue;
        }

        const c = TOKENS[maskDef];
        if (!c || !c.hasOwnProperty('regex') || !c.regex.test(valChar)) {
          is_valid = false;
          break;
        }
        valIndex++;
      }

      return is_valid;
    },

    formatDate(date: any) {
      const d = new Date(date);
      let month = '' + (d.getMonth() + 1);
      let day = '' + d.getDate();
      const year = d.getFullYear();

      if (month.length < 2) month = '0' + month;
      if (day.length < 2) day = '0' + day;

      return [year, month, day].join('-');
    },

    formatDateWithPattern(strDate: string, dateFormat: string) {
      if (!strDate) {
        return '';
      }

      if (!dateFormat) {
        dateFormat = dateFormates.date_only;
      }

      return date.formatDate(strDate, dateFormat);
    },

    getDatePattern() {
      return dateInput.getFormat(this.getDocDateFormat());
    },

    compareDates(strDate1: string, strDate2: string) {
      if (!strDate1 || !strDate2) {
        return false;
      }

      return new Date(strDate1) >= new Date(strDate2);
    },

    dateDifferenceInDays(strDate1: string, strDate2: string) {
      if (!strDate1 || !strDate2) {
        return 0;
      }

      const differenceInTime =
        new Date(strDate1).getTime() - new Date(strDate2).getTime();

      return Math.round(differenceInTime / (1000 * 3600 * 24));
    },

    dateDifferenceInMiliSec(strDate1: string, strDate2: string) {
      if (!strDate1 || !strDate2) {
        return 0;
      }

      return new Date(strDate1).getTime() - new Date(strDate2).getTime();
    },

    isDate(dateString: string) {
      return !isNaN(Date.parse(dateString));
    },

    getDocDateFormat() {
      const settings_store = useSettingStore();
      const pref = settings_store.user_settings[date_pattern_setting];
      return pref ? String(pref) : dateFormates.date_only;
    },

    getJsDateFormate() {
      return dateInput.getFormat(this.getDocDateFormat());
    },

    formatDateAccordingToPreference(
      dateString: string,
      showDateOnly = false,
      defaultValue = '-',
    ) {
      if (!dateString) {
        return defaultValue;
      } else if (!this.isDate(dateString)) {
        return dateString;
      }

      const datePattern = dateInput.getFormat(this.getDocDateFormat());

      // Pure date strings (no time component) must never be timezone-shifted.
      // `new Date("2026-02-27")` parses as UTC midnight, and Quasar then
      // renders it in local time — shifting the day backward west of UTC.
      // Anchor to local midnight by passing "YYYY-MM-DD HH:mm:ss" instead,
      // and always use the date-only mask since there's no real time value.
      if ( typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString) ) {
        return this.formatDateWithPattern(`${dateString} 00:00:00`, datePattern);
      }

      const dateFormat = showDateOnly ? datePattern : `${datePattern} HH:mm:ss`;
      return this.formatDateWithPattern(dateString, dateFormat);
    },

    formatDateTimeZone(strDate: string, isDateOnly = false) {
      if (!strDate) {
        return '-';
      }

      // Try to parse as Date
      let dateObj: Date;
      try {
        // Ensure UTC if missing timezone
        if (!/Z$|[+-]\d{2}:?\d{2}$/.test(strDate)) {
          dateObj = new Date(strDate + 'Z'); // force UTC
        } else {
          dateObj = new Date(strDate);
        }

        if (isNaN(dateObj.getTime())) {
          return strDate; // fallback if invalid date
        }
      } catch {
        return strDate;
      }

      // Format into local timezone
      const year = dateObj.getFullYear();
      const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
      const day = dateObj.getDate().toString().padStart(2, '0');
      const hours = dateObj.getHours().toString().padStart(2, '0');
      const minutes = dateObj.getMinutes().toString().padStart(2, '0');
      const seconds = dateObj.getSeconds().toString().padStart(2, '0');

      let formatted;
      let dateFormat;

      if (isDateOnly) {
        formatted = `${year}-${month}-${day}`;
        dateFormat = dateInput.getFormat(this.getDocDateFormat());
      } else {
        formatted = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        dateFormat = dateInput.getFormat(this.getDocDateFormat()) + ' HH:mm:ss';
      }

      if (!dateFormat || dateFormat == 'undefined') {
        return formatted;
      }
      return this.formatDateWithPattern(formatted, dateFormat);
    },

    formatSecondsToHoursMinutes(value: number) {
      const totalSeconds = Math.floor(value);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      const parts: string[] = [];
      if (hours > 0) parts.push(`${hours}h`);
      if (minutes > 0) parts.push(`${minutes}m`);
      parts.push(`${seconds}s`);

      return parts.join(' ');
    },

    /**
     * Format duration in seconds to human-readable format with smart unit selection.
     *
     * Rules:
     * - < 60 seconds: show seconds only (e.g., "45s")
     * - >= 60 seconds and < 1 hour: show minutes and seconds (e.g., "5m 30s")
     * - >= 1 hour and < 1 day: show hours and minutes only, NO seconds (e.g., "2h 15m")
     * - >= 1 day: show days and hours only, NO minutes (e.g., "3d 5h")
     *
     * @param value - Duration in seconds
     * @returns Formatted duration string
     */
    formatDuration(value: number): string {
      if (typeof value !== 'number' || isNaN(value) || value < 0) {
        return '<1s';
      }

      const totalSeconds = Math.floor(value);

      // Value > 0 but rounds to 0 seconds: show <1s
      if (totalSeconds === 0) {
        return '<1s';
      }

      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      // >= 1 day: show days and hours only
      if (days > 0) {
        if (hours > 0) {
          return `${days}d ${hours}h`;
        }
        return `${days}d`;
      }

      // >= 1 hour: show hours and minutes only (NO seconds)
      if (hours > 0) {
        if (minutes > 0) {
          return `${hours}h ${minutes}m`;
        }
        return `${hours}h`;
      }

      // >= 60 seconds: show minutes and seconds
      if (minutes > 0) {
        if (seconds > 0) {
          return `${minutes}m ${seconds}s`;
        }
        return `${minutes}m`;
      }

      // < 60 seconds: show seconds only
      return `${seconds}s`;
    },

    // Converts any date string to universal format (YYYY-MM-DD)
    formatDateToUniversal(dateStr: string) {
      if (!dateStr) {
        return '-';
      }

      // YYYY-MM-DD or YYYY/MM/DD
      if (dateStr.match(/^\d{4}[/-]\d{2}[/-]\d{2}$/)) {
        return dateStr.replace(/\//g, '-');
      }

      // Get user's date format preference to correctly parse ambiguous formats
      const userPattern = this.getDatePattern();

      // MM/DD/YYYY (US format)
      if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/) && userPattern === 'MM/DD/YYYY') {
        const dateParts = dateStr.split('/');
        return `${dateParts[2]}-${dateParts[0]}-${dateParts[1]}`;
      }

      // DD/MM/YYYY (European format)
      if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        const dateParts = dateStr.split('/');
        return `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
      }

      // DD.MM.YYYY
      if (dateStr.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
        const dateParts = dateStr.split('.');
        return `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;
      }

      if (!this.isDate(dateStr)) {
        return dateStr;
      }

      return this.formatDate(dateStr);
    },
  },
};
